import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import {
  amountFormatter,
  deleteAuctionEvent,
  getAuctionEventsWithPagination,
  updateAuctionEventStatus,
} from "services";
import { DateTime } from "luxon";
import { ColumnDef } from "@tanstack/react-table";
import EventDataTable from "./EventsDataTable";
import { useSelector } from "react-redux";
import { EllipsisVertical, XIcon, EyeIcon, EditIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../../../shadcn/components/ui/dropdown-menu";
import Pusher from "pusher-js";
import { useRouter } from "next/router";
import DocumentIcon from "../../../icons/DocumentIcon";
import CheckIcon from "../../../icons/CheckIcon";
import { useEventsStatusQueue } from "../../../hoc/useEventsStatusQueue";
import ViewDetailModal from "./viewDetail/ViewDetailModal";
import EventsFilterLeft from "./EventsFilterLeft";

export type tableData = {
  [x: string]: any;
  id: string;
};

type BankerAuctionEventsWrapperProps = {
  eventTypes?: string[];
};

const getStatusCode = (status: string) => {
  switch (status) {
    case "Upcoming":
      return "accepted";
    case "Live":
      return "live";
    case "End":
      return "end";
    case "To Be Approved":
      return "pending_bid_approval";
    case "Success":
      return "success";
    case "Bid Rejected":
      return "bid_rejected";
    case "Failed":
      return "failed";
    case "On Hold":
      return "holding_bid_approval";
    case "Cancelled":
      return "cancelled";
    default:
      return "";
  }
};

export default function BankerAuctionEventsWrapper({
  eventTypes,
}: BankerAuctionEventsWrapperProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [statusChangeLoader, setStatusChangeLoader] = useState(false);
  const [events, setEvents] = useState<any>([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState<number | null>(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectEventUuid, setRejectEventUuid] = useState("");
  const [cancelEventUuid, setCancelEventUuid] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showViewDetailModal, setShowViewDetailModal] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const eventQueueRef = useRef<any[]>([]);
  const router = useRouter();

  const statuses = [
    "All",
    "Upcoming",
    "Live",
    "End",
    "To Be Approved",
    "Success",
    "Bid Rejected",
    "Failed",
    "On Hold",
    "Cancelled",

    // "Rejected",
    // "Accepted",
    // "Failed",
  ];

  const StatusMap = {
    Upcoming: "accepted",
    Live: "live",
    End: "end",
    "To Be Approved": "pending_bid_approval",
    Success: "success",
    "Bid Rejected": "bid_rejected",
    Failed: "failed",
    "On Hold": "holding_bid_approval",
    Cancelled: "cancelled",
  };

  const currentBranch = useSelector(
    (state: any) => state.bankerAuth.currentBranch,
  );

  const userProfile = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  const handleGetAuctionEvents = useCallback(
    async (shouldShowNextPage: boolean) => {
      if (isFetchingMore || (shouldShowNextPage && page >= totalPages)) return;

      setIsFetchingMore(true);
      const currentPage = shouldShowNextPage ? page + 1 : 1;

      const preparedPayload: any = {
        startDate: startDate
          ? new Date(
              Date.UTC(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0,
                0,
                0,
                0,
              ),
            ).toISOString()
          : "",
        endDate: endDate
          ? new Date(
              Date.UTC(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                23,
                59,
                59,
                999,
              ),
            ).toISOString()
          : "",
        status: getStatusCode(selectedStatus),
        page: currentPage,
        eventTypes,
      };

      try {
        if (!shouldShowNextPage) setIsLoading(true);
        const res: any = await getAuctionEventsWithPagination(preparedPayload);

        setTotalPages(res.meta.total_page);
        setTotalEvents(res.meta.total);
        setPage(currentPage);

        setEvents((prev: any) =>
          shouldShowNextPage ? [...prev, ...res.data] : res.data,
        );
      } catch (error) {
        console.error("Error fetching auction events:", error);
      } finally {
        if (!shouldShowNextPage) {
          setIsLoading(false);
        }
        setIsFetchingMore(false);
      }
    },
    [
      page,
      totalPages,
      startDate,
      endDate,
      isFetchingMore,
      selectedStatus,
      eventTypes,
    ],
  );

  useEffect(() => {
    if (page < totalPages && !isFetchingMore && !isLoading) {
      const timer = setTimeout(() => {
        handleGetAuctionEvents(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [page, totalPages, isFetchingMore, isLoading]);

  useEventsStatusQueue<any>(setEvents, eventQueueRef, 200);

  const filteredRecords = useMemo(() => {
    if (!selectedStatus || selectedStatus === "All") {
      return events;
    }
    const mappedStatus = StatusMap[selectedStatus as keyof typeof StatusMap];
    return events.filter((record: any) => record.status === mappedStatus);
  }, [events, selectedStatus]);

  useEffect(() => {
    handleGetAuctionEvents(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch, startDate, endDate, eventTypes]);

  useEffect(() => {
    if (page > 0 && page < totalPages) {
      handleGetAuctionEvents(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  useEffect(() => {
    Pusher.logToConsole = false;
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    });

    const channelId = `update-dashboard`;
    const channelTwoId = `dashboard`;
    const channel = pusher.subscribe(channelId);
    const channelTwo = pusher.subscribe(channelTwoId);
    const channelTwoEventName = `dashboard-event-updated-${currentBranch?.value}`;

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        eventQueueRef.current.push({
          uuid: data.event_uuid,
          newStatus: "live",
        });
      }
    });

    channelTwo.bind(channelTwoEventName, (data: any) => {
      if (data) {
        console.log("dashboard-event-updated::> ", channelTwoEventName, data);
        eventQueueRef.current.push({
          uuid: data.event_uuid,
          newStatus: data.status,
        });
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        eventQueueRef.current.push({ uuid: data.event_uuid, newStatus: "end" });
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
    });

    return function cleanup() {
      pusher.disconnect();
    };
  }, [currentBranch]);

  const handleAcceptEvent = async (eventUuid: string) => {
    try {
      setStatusChangeLoader(true);
      const data: any = await updateAuctionEventStatus(
        eventUuid,
        "accepted",
        "",
      );
      if (data) {
        router.reload();
      }

      setStatusChangeLoader(false);
    } catch (error: any) {
      setStatusChangeLoader(false);
      console.log("check here error", error);
    }
  };

  const handleRejectEvent = async (cancellationReason: string) => {
    try {
      if (rejectEventUuid === "") return;
      setStatusChangeLoader(true);
      const data: any = await updateAuctionEventStatus(
        rejectEventUuid,
        "rejected",
        cancellationReason,
      );
      if (data) {
        router.reload();
      }
      setStatusChangeLoader(false);
    } catch (error: any) {
      setStatusChangeLoader(false);
      console.log("check here error", error);
    }
  };
  const handleDeleteEvent = async (reason: string) => {
    try {
      if (cancelEventUuid === "") return;
      setStatusChangeLoader(true);
      const data: any = await updateAuctionEventStatus(
        cancelEventUuid,
        "cancelled",
        reason,
      );
      if (data) {
        router.reload();
      }
      setStatusChangeLoader(false);
    } catch (error: any) {
      setStatusChangeLoader(false);
      console.log("check here error", error);
    }
  };

  const CancelEventModal = () => {
    const [cancellationReason, setCancellationReason] = useState("");
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <div className="bg-white shadow-2xl  w-full max-w-lg p-6">
          <div className="w-full flex justify-between items-center  pb-3">
            <p className="font-openSans font-semibold text-web-body-lg leading-5 text-brand-bgBlack">
              Cancel Event
            </p>
            <button
              onClick={() => {
                setShowCancelModal(false);
              }}
              className="text-gray-500 bg-brand-bgLightGrey rounded-full p-1.5 hover:text-gray-700"
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className={` relative w-full `}>
            <textarea
              id="name"
              value={cancellationReason}
              onChange={(e: any) => {
                setCancellationReason(e.target.value);
              }}
              className={`bg-white   py-4 peer pl-4 autofill:bg-white focus:border-brand-orange focus:outline-none focus:ring-0 pb-[6px] min-h-[120px]  outline-none  border border-[#EEEEEE]  w-full   font-openSans font-normal  leading-4 text-brand-textLightGrey `}
            />
            <label
              htmlFor="name"
              className={`absolute font-openSans font-normal text-web-body-sm leading-3 mid:text-web-body-lg mid:leading-5 ${
                !cancellationReason
                  ? "text-brand-borderGrey"
                  : "text-transparent hidden"
              } left-4 top-4 pointer-events-none transition-all duration-300 peer-placeholder-shown:top-1.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-[10px] peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:leading-[13px] peer-focus:text-brand-orange peer-valid:top-1.5 peer-valid:text-[10px] peer-valid:leading-[13px] peer-valid:text-brand-borderGrey`}
            >
              Reason For Cancelling the Event
            </label>
          </div>

          <div className="w-full flex justify-center mt-6">
            <button
              disabled={statusChangeLoader}
              className={`bg-brand-borderGrey text-white px-6 py-2 lg:w-[225px] rounded-lg hover:bg-brand-textGrey `}
              onClick={() => {
                handleDeleteEvent(cancellationReason);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const columns: ColumnDef<tableData>[] = [
    {
      id: "uuid",
      accessorKey: "uuid",
    },
    {
      id: "approver",
      accessorKey: "approver",
    },
    {
      accessorKey: "listing_id",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Event ID
        </Button>
      ),
      cell: ({ row }) => (
        <div className="lowercase min-w-16">{row.getValue("listing_id")}</div>
      ),
    },
    {
      accessorKey: "borrower_name",
      header: () => <div className="">Borrower Name</div>,
      cell: ({ row }) => {
        return (
          <div className="font-medium min-w-[100px]">
            {(row.getValue("borrower_name") as string)?.slice(0, 25) || "N/A"}
          </div>
        );
      },
    },
    {
      accessorKey: "start_date_time",
      header: "Start",
      cell: ({ row }) => (
        <div className="capitalize text-xs break-keep min-w-[200px]">
          {row.getValue<string>("start_date_time") ? (
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3 break-keep">
                {DateTime.fromISO(row.getValue<string>("start_date_time"))
                  .setZone("Asia/Kolkata")
                  .toFormat("dd MMM yyyy")}
              </div>
              <div className="px-3 text-xs break-keep">
                {`${DateTime.fromISO(row.getValue<string>("start_date_time"))
                  .setZone("Asia/Kolkata")
                  .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: "end_date_time",
      header: "End",
      cell: ({ row }) => (
        <div className="capitalize text-xs break-keep min-w-[200px]">
          {row.getValue<string>("end_date_time") ? (
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3 break-keep">
                {DateTime.fromISO(row.getValue<string>("end_date_time"))
                  .setZone("Asia/Kolkata")
                  .toFormat("dd MMM yyyy")}
              </div>
              <div className="px-3 text-xs break-keep">
                {`${DateTime.fromISO(row.getValue<string>("end_date_time"))
                  .setZone("Asia/Kolkata")
                  .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: "reserve_price",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-20 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Reserve price
        </Button>
      ),
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("reserve_price"));
        return <div className="min-w-20">₹{amountFormatter(amount)}</div>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div
          className={`capitalize px-2 py-1 rounded-full text-center text-xs font-normal
    ${
      row.getValue("status") === "live"
        ? "bg-[#FF752C] text-white"
        : row.getValue("status") === "pending_bid_approval"
        ? "bg-yellow-100 text-yellow-700"
        : row.getValue("status") === "end"
        ? "bg-gray-100 text-gray-700"
        : [
            "cancelled",
            "failed",
            "bid_rejected",
            "holding_bid_approval",
          ].includes(row.getValue("status"))
        ? "bg-red-50 text-red-500"
        : ["success"].includes(row.getValue("status"))
        ? "bg-green-50 text-green-500"
        : row.getValue("status") === "accepted"
        ? "bg-[#e0f2fe] text-[#075985]"
        : "bg-gray-200 text-gray-800"
    }
  `}
        >
          {row.getValue("status")
            ? Object.keys(StatusMap).find(
                (key) =>
                  StatusMap[key as keyof typeof StatusMap] ===
                  row.getValue("status"),
              ) || ""
            : "N/A"}
        </div>
      ),
    },
    // {
    //   accessorKey: "report",
    //   header: "Reports",
    //   cell: ({ row }) => {
    //     return row.getValue("status") === "end" ? (
    //       <div
    //         onClick={() => {
    //           router.push(`/dashboard/reports/${row.getValue("listing_id")}`);
    //         }}
    //         className="flex items-center space-x-1 hover:cursor-pointer"
    //       >
    //         <DocumentIcon />
    //         <p className="font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack underline">
    //           View
    //         </p>
    //       </div>
    //     ) : (
    //       <p>NA</p>
    //     );
    //   },
    // },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const buttonText =
          row.getValue("status") === "live" ? "View" : "Details";
        const lisitingId = row.getValue("listing_id");

        const directLink =
          row.getValue("status") === "live"
            ? `/dashboard/live-events/${row.original.uuid}`
            : `/dashboard/events/${lisitingId}`;
        return (
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (row.getValue("status") === "live") {
                    window.open(directLink);
                  } else {
                    setSelectedEventData(row.original);
                    setShowViewDetailModal(true);
                  }
                }}
              >
                <EyeIcon className="w-4 h-4" />
              </button>
              <button
                disabled={[
                  "live",
                  "end",
                  "failed",
                  "success",
                  "cancelled",
                  "bid_rejected",
                  "pending_bid_approval",
                  "holding_bid_approval",
                ].includes(row.getValue("status"))}
                onClick={() =>
                  router.push(`/dashboard/events/edit/${lisitingId}`)
                }
              >
                <EditIcon className="w-4 h-4" />
              </button>
              <button
                disabled={[
                  "end",
                  "failed",
                  "success",
                  "cancelled",
                  "bid_rejected",
                  "pending_bid_approval",
                  "holding_bid_approval",
                ].includes(row.getValue("status"))}
                className="text-red-700"
                onClick={() => {
                  setCancelEventUuid(row.getValue("uuid"));
                  setShowCancelModal(true);
                }}
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <EllipsisVertical className="my-auto hover:cursor-pointer" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white rounded-[8px]"
              >
                {" "}
                <DropdownMenuItem
                  onClick={() => router.push(directLink)}
                  // className={`${
                  //   currentBranch?.team?.["team_members*permissions"]?.role ===
                  //     "approver" &&
                  //   row.getValue("status") === "pending" &&
                  //   row.getValue("approver") === userProfile?.uuid
                  //     ? ""
                  //     : "hidden"
                  // }`}
                >
                  View
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={row.getValue("status") === "live"}
                  onClick={() =>
                    router.push(`/dashboard/events/edit/${lisitingId}`)
                  }
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={
                    row.getValue("status") === "live" ||
                    row.getValue("status") === "cancelled" ||
                    row.getValue("status") === "end"
                  }
                  onClick={() => {
                    setCancelEventUuid(row.getValue("uuid"));
                    setShowCancelModal(true);
                  }}
                >
                  Cancel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu> */}
          </div>
        );
      },
    },
  ];

  const RejectionModal = () => {
    const [cancellationReason, setCancellationReason] = useState("");
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <div className="bg-white shadow-2xl  w-full max-w-lg p-6">
          <div className="w-full flex justify-between items-center  pb-3">
            <p className="font-openSans font-semibold text-web-body-lg leading-5 text-brand-bgBlack">
              Reject Event
            </p>
            <button
              onClick={() => {
                setShowRejectionModal(false);
              }}
              className="text-gray-500 bg-brand-bgLightGrey rounded-full p-1.5 hover:text-gray-700"
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          <div className={` relative w-full `}>
            <textarea
              id="name"
              value={cancellationReason}
              onChange={(e: any) => {
                setCancellationReason(e.target.value);
              }}
              className={`bg-white   py-4 peer pl-4 autofill:bg-white focus:border-brand-orange focus:outline-none focus:ring-0 pb-[6px] min-h-[120px]  outline-none  border border-[#EEEEEE]  w-full   font-openSans font-normal  leading-4 text-brand-textLightGrey `}
            />
            <label
              htmlFor="name"
              className={`absolute font-openSans font-normal text-web-body-sm leading-3 mid:text-web-body-lg mid:leading-5 ${
                !cancellationReason
                  ? "text-brand-borderGrey"
                  : "text-transparent hidden"
              } left-4 top-4 pointer-events-none transition-all duration-300 peer-placeholder-shown:top-1.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-[10px] peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:leading-[13px] peer-focus:text-brand-orange peer-valid:top-1.5 peer-valid:text-[10px] peer-valid:leading-[13px] peer-valid:text-brand-borderGrey`}
            >
              Reason For Rejecting the Event
            </label>
          </div>

          <div className="w-full flex justify-center mt-6">
            <button
              disabled={isLoading || cancellationReason === ""}
              className={`bg-brand-borderGrey text-white px-6 py-2 lg:w-[225px] ${
                cancellationReason === "" ? "cursor-not-allowed" : ""
              } rounded-lg hover:bg-brand-textGrey `}
              onClick={() => {
                handleRejectEvent(cancellationReason);
              }}
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <EventDataTable
        data={filteredRecords}
        columns={columns}
        filterComponent={
          <EventsFilterLeft
            startDate={startDate}
            endDate={endDate}
            selectedStatus={selectedStatus}
            statuses={statuses}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onStatusChange={setSelectedStatus}
          />
        }
        loading={isLoading}
        totalEvents={totalEvents}
      />

      {/* {showRejectionModal && <RejectionModal />} */}
      {showCancelModal && <CancelEventModal />}
      <ViewDetailModal
        open={showViewDetailModal}
        onOpenChange={setShowViewDetailModal}
        eventData={selectedEventData}
        router={router}
      />
    </div>
  );
}
